/**
 * One idempotency key per way of paying **and per quote**, within one checkout (GH-791, GH-809).
 *
 * The key is what makes a double tap one charge: the same request with the same key is answered
 * with the first response. A *different* request under a used key is refused — "This idempotency
 * key was already used for a different request" — and the request body names both the way of
 * paying and the signed quote. So a key may only travel again with the same method *and* the same
 * quote token:
 *
 *  - a change of method (wallet → card, saved → card) is a new key (GH-791);
 *  - a new quote on the same method is a new key (GH-809). Changing the amount, the fund or the
 *    cost support re-quotes, and a saved bank account re-quotes at the ACH rate on every press, so
 *    each of those used to send the earlier key out with a different body and dead-end;
 *  - an authorization that failed after the server answered (a wallet's confirm, a 3-D Secure
 *    step) is `reset()` by the caller, so the next press is a new attempt rather than a replay of
 *    the one that failed. The server then supersedes the unpaid attempt on the same quote (GH-791).
 *
 * Pressing the same method on the same quote again keeps the key: that is the double tap.
 */
export type PaymentKeys = {
  /**
   * The key for this way of paying on this quote with this note; a new one whenever any of them
   * changes. The note is part of the request body too (GH-793), so editing it after a press that
   * reached the server is a different request.
   */
  readonly keyFor: (
    method: string,
    quoteToken: string,
    note?: string | null,
    /** "Show on the event screen" (GH-887): in the body too, so a change is a new request. */
    showNoteOnScreen?: boolean,
  ) => string;
  /** A fresh attempt from scratch: after a cancelled sheet, a failed authorization, or a gift. */
  readonly reset: () => void;
};

export function paymentKeys(mint: () => string): PaymentKeys {
  let key = mint();
  let usedFor: string | null = null;
  return {
    keyFor(method, quoteToken, note = null, showNoteOnScreen = false) {
      // JSON keeps the parts unambiguous whatever characters any of them carries.
      const request = JSON.stringify([method, quoteToken, note, showNoteOnScreen]);
      if (usedFor !== null && usedFor !== request) key = mint();
      usedFor = request;
      return key;
    },
    reset() {
      key = mint();
      usedFor = null;
    },
  };
}
